import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { boxShadows, grey, white } from "designSystem/styles/color";
import { IconButton } from "designSystem/index";
import { ICON_SMALL_SIZE, STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import React, { ReactNode, useCallback, useRef } from "react";

const TOOLBAR_OFFSET = 20;

interface ToolbarProps {
    children: ReactNode;
}

const ToolbarBox = styled(Box)(
	({ theme }) => ({        
		alignItems: "center",
		backgroundColor: white[50],
		border: STANDARD_BORDER_STYLE,
		borderRadius: "4px",
		boxShadow: boxShadows.popper,
		display: "flex",
		flexDirection: "column",
		gap: 4,
		justifyContent: "space-between",
		margin: TOOLBAR_OFFSET + "px",
		padding: "4px",
		position: "fixed",
		zIndex: "1",
	}),
);

const Toolbar = (props: ToolbarProps) => {
	const ref = useRef<HTMLButtonElement>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	let minX: number;
	let minY: number;
	let maxX: number;
	let maxY: number;

	const startDrag = useCallback((e: React.PointerEvent) => {
		const parentContainer = rootRef.current.parentElement;
		
		if (!parentContainer) {
			return;
		}
		const TOOLBAR_WIDTH = rootRef.current.offsetWidth;
		const TOOLBAR_HEIGHT = rootRef.current.offsetHeight;

		const parentRect = parentContainer.getBoundingClientRect();
		minX = parentRect.left - TOOLBAR_OFFSET;
		minY = parentRect.top - TOOLBAR_OFFSET;
		maxX = parentRect.right - TOOLBAR_WIDTH - TOOLBAR_OFFSET;
		maxY = parentRect.bottom - TOOLBAR_HEIGHT - TOOLBAR_OFFSET;

		ref.current.onpointermove = drag;
		ref.current?.setPointerCapture(e.pointerId);
	}, []);

	const drag = useCallback((e: PointerEvent) => {
		const offsetX = e.clientX - (TOOLBAR_OFFSET + ICON_SMALL_SIZE);
		const offsetY = e.clientY - (TOOLBAR_OFFSET + ICON_SMALL_SIZE);

		// clamp the toolbar to the parent container
		const clampedX = Math.min(Math.max(offsetX, minX), maxX);
		const clampedY = Math.min(Math.max(offsetY, minY), maxY);
        
		// set the new position of the toolbar
		rootRef.current.style.left = (clampedX) + "px";
		rootRef.current.style.top = (clampedY) + "px";
		ref.current.style.cursor = "grabbing";
	}, []);

	const endDrag = useCallback((e: React.PointerEvent) => {
		ref.current.onpointermove = null;
		ref.current?.releasePointerCapture(e.pointerId);
		ref.current.style.cursor = "grab";
	}, []);

	return (
		<ToolbarBox ref={rootRef}>
			<IconButton
				customSize={16} 
				design="darkMode" 
				onPointerDown={startDrag}
				onPointerUp={endDrag} 
				ref={ref}
			>
				<MoreHorizIcon 
					sx={{
						cursor: "grab",
						"&:hover": {
							color: grey[400] + " !important",
						},
						"&:active": {
							color: grey[400] + " !important",
						},
					}}
				/>
			</IconButton>
			{props.children}
		</ToolbarBox>
	);
};

export default Toolbar;
